import React from 'react';
import { Container, Grid, Typography, Button, Menu, MenuItem, Box, Card, CardContent, LinearProgress } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from '@mui/lab';
import { Person, Assignment, CheckCircle, Event, Add } from '@mui/icons-material';
import styles from './AdminDashboard.module.css';

const Dashboard = ({ setCurrentView, dashboardData }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleAddNewClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleAddNewClose = () => {
        setAnchorEl(null);
    };

    const handleMenuItemClick = (view) => {
        setCurrentView(view);
        setAnchorEl(null);
    };

    const handleCardClick = (view) => {
        setCurrentView(view);
    };

    const pieChartData = [
        { name: 'Completed', value: dashboardData.completedInterviews },
        { name: 'Pending', value: dashboardData.interviewCount - dashboardData.completedInterviews }
    ];

    const COLORS = ['#00C49F', '#FFBB28', '#FF8042', '#0088FE'];

    return (
        <Box className={styles.dashboardContainer}>
            <Container maxWidth="lg">
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h4">Dashboard</Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddNewClick}
                        startIcon={<Add />}
                        style={{ marginLeft: 'auto' }}
                    >
                        ADD NEW
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleAddNewClose}
                    >
                        <MenuItem onClick={() => handleMenuItemClick('interviewees')}>Interviewees</MenuItem>
                        <MenuItem onClick={() => handleMenuItemClick('aiInstructions')}>AI Assistant Instructions</MenuItem>
                    </Menu>
                </Box>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                        <Card 
                            className={styles.statCard} 
                            onClick={() => handleCardClick('interviews')}
                            style={{ cursor: 'pointer' }}
                        >
                            <CardContent>
                                <Assignment fontSize="large" color="primary" />
                                <Typography variant="h5" component="div">
                                    {dashboardData.interviewCount}
                                </Typography>
                                <Typography color="textSecondary">
                                    Total Interviews
                                </Typography>
                            </CardContent>
                            <LinearProgress variant="determinate" value={(dashboardData.interviewCount / 100) * 100} />
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card 
                            className={styles.statCard}
                            onClick={() => handleCardClick('interviewees')}
                            style={{ cursor: 'pointer' }}
                        >
                            <CardContent>
                                <Person fontSize="large" color="secondary" />
                                <Typography variant="h5" component="div">
                                    {dashboardData.intervieweeCount}
                                </Typography>
                                <Typography color="textSecondary">
                                    Total Interviewees
                                </Typography>
                            </CardContent>
                            <LinearProgress variant="determinate" value={(dashboardData.intervieweeCount / 100) * 100} color="secondary" />
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card 
                            className={styles.statCard}
                            onClick={() => handleCardClick('interviews')}
                            style={{ cursor: 'pointer' }}
                        >
                            <CardContent>
                                <CheckCircle fontSize="large" style={{ color: '#00C49F' }} />
                                <Typography variant="h5" component="div">
                                    {dashboardData.completedInterviews}
                                </Typography>
                                <Typography color="textSecondary">
                                    Completed Interviews
                                </Typography>
                            </CardContent>
                            <LinearProgress variant="determinate" value={(dashboardData.completedInterviews / dashboardData.interviewCount) * 100} style={{ backgroundColor: '#00C49F' }} />
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card className={styles.statCard}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Interview Status</Typography>
                                <Box height={200}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={pieChartData}
                                                innerRadius={40}
                                                outerRadius={60}
                                                paddingAngle={5}
                                                dataKey="value"
                                            >
                                                {pieChartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card className={styles.statCard}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Interview Status Distribution</Typography>
                                <Box height={300}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={dashboardData.interviewStatusCounts}
                                                dataKey="count"
                                                nameKey="status"
                                                cx="50%"
                                                cy="50%"
                                                outerRadius={80}
                                                fill="#8884d8"
                                                label
                                            >
                                                {dashboardData.interviewStatusCounts.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card 
                            className={styles.statCard}
                            onClick={() => handleCardClick('interviews')}
                            style={{ cursor: 'pointer' }}
                        >
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Recent Interviews</Typography>
                                <Timeline>
                                    {dashboardData.recentInterviews.map((interview, index) => (
                                        <TimelineItem key={index}>
                                            <TimelineSeparator>
                                                <TimelineDot color="primary">
                                                    <Event />
                                                </TimelineDot>
                                                {index < dashboardData.recentInterviews.length - 1 && <TimelineConnector />}
                                            </TimelineSeparator>
                                            <TimelineContent>
                                                <Typography variant="body1">{`${interview.first_name} ${interview.last_name}`}</Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    {new Date(interview.timestamp).toLocaleString()}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Status: {interview.status}
                                                </Typography>
                                            </TimelineContent>
                                        </TimelineItem>
                                    ))}
                                </Timeline>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default Dashboard;